import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { PdpSharedShippingInfo } from './PdpSharedShippingInfo';
import { PdpSharedShippingInfoSidebar } from './PdpSharedShippingInfoSidebar';

import { PdpSharedShippingInfoWrapper_item$key } from './__generated__/PdpSharedShippingInfoWrapper_item.graphql';
import { PdpSharedShippingInfoWrapper_user$key } from './__generated__/PdpSharedShippingInfoWrapper_user.graphql';

type Props = {
    item: PdpSharedShippingInfoWrapper_item$key;
    user: PdpSharedShippingInfoWrapper_user$key | null;
    currency: string;
    children?: ReactNode;
    leadTime?: string | null;
    renderTooltip?: boolean;
    isClient?: boolean;
    title?: ReactNode;
    displayInline?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
    calculateLowerShippingInfo?: boolean;
    positioning?: 'sidebar' | 'seller_details';
    showShippingIcon?: boolean;
    shortLinkCta?: boolean;
    shortRequestQuote?: boolean;
    shippingInfoClass?: string;
    handleRoundPriceTracking?: () => void;
};

const PdpSharedShippingInfoWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    currency,
    children,
    leadTime,
    renderTooltip,
    isClient = false,
    title,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    itemQuantity,
    calculateLowerShippingInfo = false,
    positioning,
    showShippingIcon,
    shippingInfoClass,
    shortLinkCta,
    shortRequestQuote,
    handleRoundPriceTracking,
}) => {
    const user = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_item on Item {
                serviceId
            }
        `,
        itemRef
    );

    if (positioning === 'sidebar') {
        return (
            <PdpSharedShippingInfoSidebar
                currency={currency}
                itemId={item.serviceId || ''}
                userId={user?.serviceId || ''}
                isTrade={user?.isVerifiedTrade || false}
                loadingTitle={title}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                isClient={isClient}
                itemQuantity={itemQuantity}
                showShippingIcon={showShippingIcon}
                shippingInfoClass={shippingInfoClass}
                shortLinkCta={shortLinkCta}
                shortRequestQuote={shortRequestQuote}
                handleRoundPriceTracking={handleRoundPriceTracking}
            >
                {children}
            </PdpSharedShippingInfoSidebar>
        );
    } else {
        return (
            <PdpSharedShippingInfo
                renderTooltip={renderTooltip}
                currency={currency}
                leadTime={leadTime || ''}
                itemId={item.serviceId || ''}
                userId={user?.serviceId || ''}
                isTrade={user?.isVerifiedTrade || false}
                title={title}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                isClient={isClient}
                itemQuantity={itemQuantity}
                calculateLowerShippingInfo={calculateLowerShippingInfo}
                positioning={positioning}
                handleRoundPriceTracking={handleRoundPriceTracking}
            >
                {children}
            </PdpSharedShippingInfo>
        );
    }
};

export default PdpSharedShippingInfoWrapper;
